import type { AuthAdapter } from './adapter';
import { LocalHarnessAuthAdapter } from './localAdapter';
import { AuthFailure, SupabaseAuthAdapter } from './supabaseAdapter';

/**
 * Chooses the authentication implementation from the public build configuration.
 *
 * `local` exists only for the offline end-to-end harness and is refused in a production build,
 * mirroring the backend's own `fake auth provider is forbidden in production` guard. A build
 * shipped to staging or production can therefore only ever talk to Supabase Auth.
 */
export function createAuthAdapter(config: {
  provider: string | undefined;
  supabaseUrl: string;
  supabaseAnonKey: string;
  harnessEndpoint?: string;
  isProduction: boolean;
}): AuthAdapter {
  const provider = (config.provider ?? 'supabase').toLowerCase();
  if (provider === 'local') {
    if (config.isProduction) {
      throw new Error('the harness auth provider is forbidden in a production build');
    }
    return new LocalHarnessAuthAdapter(config.harnessEndpoint ?? '/__harness/session');
  }
  if (!config.supabaseUrl || !config.supabaseAnonKey) {
    return new UnconfiguredAuthAdapter();
  }
  return new SupabaseAuthAdapter(config.supabaseUrl, config.supabaseAnonKey);
}

/**
 * Stand-in used when no Supabase project is configured. It refuses every attempt with a clear
 * message instead of failing obscurely inside the provider client, and it reports honestly on
 * the login screen that the identity provider is not connected.
 */
class UnconfiguredAuthAdapter implements AuthAdapter {
  readonly name = 'not_configured';
  readonly supportsRecovery = false;
  async restore() {
    return { status: 'signed_out', email: null, aal: null, hasToken: false } as const;
  }
  async signInWithPassword(): Promise<never> {
    throw new AuthFailure('provider_not_configured');
  }
  async requestPasswordRecovery(): Promise<never> {
    throw new AuthFailure('provider_not_configured');
  }
  async beginPasswordRecovery(): Promise<never> {
    throw new AuthFailure('provider_not_configured');
  }
  async completePasswordRecovery(): Promise<never> {
    throw new AuthFailure('provider_not_configured');
  }
  async endPasswordRecovery() {
    /* nothing was ever established */
  }
  async signOut() {
    /* nothing to sign out of */
  }
  async getAccessToken() {
    return null;
  }
  onChange() {
    return () => {};
  }
}
